import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, CheckCircle2, BookOpen, Store, Percent, Users, TrendingUp, Sparkles, Eye, EyeOff, Wand2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditorialLanding = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ editorial_name: '', username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copiedPassword, setCopiedPassword] = useState(false);

  const generateSecurePassword = () => {
    const length = 12;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%&*_+-=';
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setGeneratedPassword(newPassword);
    setForm((prev) => ({ ...prev, password: newPassword }));
    setShowPassword(true);
    setCopiedPassword(false);
  };

  const handleCopyPassword = async () => {
    if (generatedPassword) {
      try {
        await navigator.clipboard.writeText(generatedPassword);
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      } catch (err) {
        console.error('Error copying password:', err);
      }
    }
  };

  const getPasswordStrength = (pw) => {
    if (!pw) return { score: 0, label: '', color: '', pct: 0 };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const levels = [
      { label: 'Muy débil', color: 'bg-red-500', text: 'text-red-400', pct: 20 },
      { label: 'Débil', color: 'bg-orange-500', text: 'text-orange-400', pct: 40 },
      { label: 'Media', color: 'bg-yellow-500', text: 'text-yellow-400', pct: 60 },
      { label: 'Fuerte', color: 'bg-lime-500', text: 'text-lime-400', pct: 80 },
      { label: 'Muy fuerte', color: 'bg-emerald-500', text: 'text-emerald-400', pct: 100 },
    ];
    return { score, ...levels[Math.min(score, 5) - 1 < 0 ? 0 : Math.min(score, 5) - 1] };
  };
  const pwStrength = getPasswordStrength(form.password);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.editorial_name || !form.username || !form.email || !form.password) {
      return toast.error('Completa todos los campos');
    }
    setLoading(true);
    try {
      await axios.post(`${API}/editorial/register`, form);
      const res = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password });
      const { token, user } = res.data;
      login(token, user);
      toast.success('¡Bienvenida editorial! Tu cuenta está lista.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo completar el registro');
    } finally {
      setLoading(false);
    }
  };

  const liteFeatures = [
    'Reporte de ventas directas de tus obras',
    'Registra tus obras por metadata (ISBN, título, autor)',
    'Abre cuentas Autor-Lite para tus autores (tus afiliados)',
    'Tus primeros 10 ebooks, sin costo',
  ];
  const premiumFeatures = [
    'Todo lo de Lite, más:',
    'Desglose de ventas por tienda',
    'Cobra en nombre de tus autores',
    'Asigna un split (%) a cada autor',
    'Acceso al Pool de Autores y beneficios Premium',
  ];

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      {/* Top bar con logo oficial Viubux + isotipo a la derecha */}
      <header className="border-b border-white/10 bg-[#0a0a12]/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img
            src="/viubux_logo_web.png"
            alt="Viubux Connect"
            data-testid="editorial-viubux-logo"
            className="h-8 md:h-9 w-auto object-contain brightness-0 invert"
            style={{ maxWidth: '160px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <img
            src="/viubux_isotype_white.png"
            alt="Viubux"
            data-testid="editorial-viubux-isotype"
            className="h-9 md:h-11 w-auto object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-600/20 via-transparent to-orange-900/10" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-300 text-sm mb-6">
              <Building2 className="w-4 h-4" /> Programa para Editoriales
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl gotham-bold leading-tight">
              Publica, distribuye y <span className="text-amber-400">monetiza</span> tu catálogo
            </h1>
            <p className="mt-5 text-white/70 text-base max-w-lg">
              Únete al Universo Viubux como editorial. Registra tus obras, gestiona a tus autores
              y cobra por tu catálogo — con transparencia total sobre tus ventas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#registro">
                <Button data-testid="editorial-landing-cta" className="bg-amber-500 hover:bg-amber-400 text-black gotham-bold px-6 py-6 rounded-xl text-base">
                  <Sparkles className="w-5 h-5 mr-2" /> Crear cuenta gratis
                </Button>
              </a>
              <Button variant="outline" onClick={() => navigate('/')}
                className="border-white/20 text-white/80 hover:bg-white/10 px-6 py-6 rounded-xl text-base">
                Volver al inicio
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: BookOpen, t: 'Catálogo', d: 'Registra por metadata' },
              { icon: Store, t: 'Multi-tienda', d: 'Desglose por tienda' },
              { icon: Percent, t: 'Split autor', d: 'Comparte el %' },
              { icon: Users, t: 'Tus autores', d: 'Cuentas afiliadas' },
            ].map((f, i) => (
              <Card key={i} className="bg-white/[0.04] border-white/10">
                <CardContent className="p-5">
                  <f.icon className="w-7 h-7 text-amber-400 mb-3" />
                  <div className="metallic-silver gotham-bold text-lg">{f.t}</div>
                  <div className="text-white/70 text-sm">{f.d}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Tiers */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="metallic-silver text-3xl md:text-4xl gotham-bold text-center mb-3">Dos niveles para tu editorial</h2>
        <p className="text-center text-white/60 mb-10 text-base">Empieza gratis en Lite y sube a Premium cuando quieras.</p>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-7">
              <div className="text-white/60 text-sm">Editorial</div>
              <div className="metallic-silver text-4xl gotham-bold mt-1">Lite</div>
              <div className="text-white/50 text-sm mt-1">Gratis — primeros 10 ebooks</div>
              <ul className="mt-6 space-y-3">
                {liteFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-b from-amber-500/10 to-transparent border-amber-500/30 relative">
            <div className="absolute -top-3 left-7 bg-amber-500 text-black text-xs gotham-bold px-3 py-1 rounded-full flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> Recomendado
            </div>
            <CardContent className="p-7">
              <div className="text-amber-300 text-sm">Editorial</div>
              <div className="metallic-silver text-4xl gotham-bold mt-1">Premium</div>
              <div className="text-white/50 text-sm mt-1">$4,997 MXN anual + $199 MXN/mes</div>
              <ul className="mt-6 space-y-3">
                {premiumFeatures.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${i === 0 ? 'text-amber-300 gotham-bold' : 'text-white/80'}`}>
                    {i !== 0 && <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />} {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Línea divisoria entre paquetes y formulario */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      </div>

      {/* Registration - recuadro amplio, casi a la par de los paquetes */}
      <div id="registro" className="max-w-3xl mx-auto px-6 pt-16 pb-16">
        <Card className="bg-white/[0.04] border-white/10">
          <CardContent className="p-8 md:p-10">
            <h3 className="metallic-silver text-3xl md:text-4xl gotham-bold mb-2">Crea tu cuenta de editorial</h3>
            <p className="text-white/60 text-base mb-8">Empiezas en el nivel Lite (gratis). Puedes subir a Premium después.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { k: 'editorial_name', ph: 'Nombre de la editorial', type: 'text' },
                  { k: 'username', ph: 'Usuario (para tu URL)', type: 'text' },
                  { k: 'email', ph: 'Correo electrónico', type: 'email' },
                ].map((f) => (
                  <input key={f.k} type={f.type} placeholder={f.ph} value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    data-testid={`editorial-register-${f.k}`}
                    className={`w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 ${f.k === 'email' ? 'sm:col-span-2' : ''}`} />
                ))}
              </div>

              {/* Contraseña con generador y ojito (misma lógica que Connect) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-white/80">Contraseña</label>
                  <button type="button" onClick={handleGeneratePassword}
                    data-testid="editorial-generate-password-btn"
                    className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 font-medium transition-colors">
                    <Wand2 className="w-3 h-3" /> Generar automática
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    value={form.password}
                    minLength={8}
                    onChange={(e) => { setForm({ ...form, password: e.target.value }); setGeneratedPassword(''); }}
                    data-testid="editorial-register-password"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    data-testid="editorial-toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-amber-400 transition-colors">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {generatedPassword ? (
                  <div className="mt-2 flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    <code className="text-amber-200 text-sm font-mono break-all" data-testid="editorial-generated-password">{generatedPassword}</code>
                    <button type="button" onClick={handleCopyPassword}
                      data-testid="editorial-copy-password-btn"
                      className="flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 shrink-0">
                      {copiedPassword ? <><Check className="w-3.5 h-3.5" /> ¡Copiada!</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                    </button>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-white/40">Debe incluir: mayúscula, minúscula, número y símbolo (!@#$%...)</p>
                )}

                {/* Medidor de fuerza de contraseña */}
                {form.password && (
                  <div className="mt-2" data-testid="editorial-password-strength">
                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full ${pwStrength.color} transition-all duration-300`} style={{ width: `${pwStrength.pct}%` }} />
                    </div>
                    <p className={`text-xs mt-1 ${pwStrength.text}`} data-testid="editorial-password-strength-label">
                      Seguridad: {pwStrength.label}
                    </p>
                  </div>
                )}
              </div>

              <Button type="submit" disabled={loading}
                data-testid="editorial-register-submit"
                className="w-full bg-amber-500 hover:bg-amber-400 text-black gotham-bold py-6 rounded-xl">
                {loading ? 'Creando cuenta...' : 'Crear cuenta de editorial'}
              </Button>
            </form>
            <div className="mt-5 flex flex-col items-center gap-2">
              <button type="button"
                data-testid="editorial-login-link"
                onClick={() => navigate('/login')}
                className="text-sm text-white/70 hover:text-amber-400 transition-colors">
                ¿Ya tienes cuenta? <span className="gotham-bold text-amber-400">Inicia sesión</span>
              </button>
              <button type="button"
                data-testid="editorial-forgot-password-link"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-white/50 hover:text-amber-400 transition-colors">
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Parte del Universo Viubux - logo oficial grande (debajo del formulario) */}
      <div className="border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-14 flex flex-col items-center text-center">
          <img
            src="/vbx-connect-white.png"
            alt="Viubux"
            data-testid="editorial-viubux-brand-logo"
            className="h-16 md:h-24 w-auto object-contain mb-5"
            style={{ maxWidth: '440px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="text-white/60 text-sm max-w-xl">
            Tu editorial forma parte del <strong className="text-white">Universo Viubux</strong>:
            distribución, audiolibros, ebooks y una comunidad que impulsa tu catálogo.
          </p>
          <div className="flex items-center justify-center gap-8 mt-8 flex-wrap opacity-90">
            <a href="https://play.viubux.com" target="_blank" rel="noreferrer" data-testid="editorial-play-logo">
              <img src="/vbx-play-ondark.png" alt="Viubux Play" className="h-9 md:h-10 w-auto object-contain hover:scale-105 transition-transform" onError={(e)=>{e.target.style.display='none';}} />
            </a>
            <a href="https://reader.viubux.com" target="_blank" rel="noreferrer" data-testid="editorial-reader-logo">
              <img src="/vbx-reader-ondark.png" alt="Viubux Reader" className="h-9 md:h-10 w-auto object-contain hover:scale-105 transition-transform" onError={(e)=>{e.target.style.display='none';}} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorialLanding;
